import { Link } from '@tanstack/react-router';
import type { Theme } from '@jeraisonne/db';
import { SCHOOL_LEVELS } from '@jeraisonne/db';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const VISIBILITY_LABEL: Record<Theme['visibility'], { label: string; className: string }> = {
  private: { label: 'Privé', className: 'bg-ink-100 text-ink-700' },
  pending_review: { label: 'En modération', className: 'bg-accent-50 text-accent-700' },
  public: { label: 'Public', className: 'bg-xp-100 text-xp-700' },
};

export function ThemeCard({
  theme,
  to,
  showVisibility = false,
}: {
  theme: Theme;
  to: string;
  showVisibility?: boolean;
}) {
  return (
    <Link
      to={to}
      className="border-ink-200 hover:border-ink-300 group flex min-h-[2.75rem] flex-col overflow-hidden rounded-2xl border bg-white transition active:scale-[0.98]"
    >
      {theme.illustration_url ? (
        <img
          src={theme.illustration_url}
          alt={theme.title}
          className="aspect-video w-full object-cover"
        />
      ) : (
        <div className="from-primary-100 to-primary-200 aspect-video w-full bg-gradient-to-br" />
      )}
      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <p className="font-display text-ink-900 text-[14px] font-semibold">{theme.title}</p>
        {theme.short_description && (
          <p className="text-ink-500 mt-1 line-clamp-2 font-sans text-xs">
            {theme.short_description}
          </p>
        )}
        <div className="text-ink-400 mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span>
            {theme.min_level
              ? `Dès la ${LEVEL_LABEL[theme.min_level] ?? theme.min_level}`
              : 'Tous niveaux'}
          </span>
          <span>·</span>
          <span>
            {theme.exercise_count} exo{theme.exercise_count > 1 ? 's' : ''}
          </span>
          {showVisibility && (
            <>
              <span>·</span>
              <span
                className={`rounded-full px-2 py-0.5 font-medium ${VISIBILITY_LABEL[theme.visibility].className}`}
              >
                {VISIBILITY_LABEL[theme.visibility].label}
              </span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
